import { Utils } from 'core/utils';
import { AreaSizePredicate, ControllerOptions, NormalizedControllerOptions,
         normalizedOptions, TextApplianceResult, TextController,
         TextControllerBase, TextEditingResult
       } from './text.controller';
import { TextControllerMethods, TextEditingRefusalReason
       } from './text.controller.methods';
import { fittedToArea } from './text.layout';
import { FontDescription, Text, TextArea, TextChunk, TextEffects, updateFont
       } from './text.model';

function fitChangedModelResizingArea(
  model: Text,
  changedModel: Text,
  allowsResizeTo: AreaSizePredicate,
  minFontSize: number
): TextEditingResult
{
  const fitted = fittedToArea(changedModel, allowsResizeTo, minFontSize);

  if (!fitted) {
    return <TextEditingResult> {
      type: TextApplianceResult.Failure,
      model: model,
      reason: TextEditingRefusalReason.TextTooHigh,
    };
  }

  return <TextEditingResult> {
    type: TextApplianceResult.Success,
    model: fitted,
    reason: fitted.fontSize < changedModel.fontSize
      ? TextEditingRefusalReason.TextTooHigh
      : TextEditingRefusalReason.None,
  };
}

function fitChangedModel(
  model: Text,
  changedModel: Text,
  options: NormalizedControllerOptions
): TextEditingResult
{
  const unicodeCheckResult =
      TextControllerMethods.checkForInvalidSymbols(changedModel);
  if (unicodeCheckResult !== TextEditingRefusalReason.None) {
    return <TextEditingResult> {
      type: TextApplianceResult.Failure,
      reason: TextEditingRefusalReason.CharacterNotSupported,
      model: model,
      position: unicodeCheckResult
    };
  }

  if (options.allowsResizeTo) {
    return fitChangedModelResizingArea(
        model, changedModel, options.allowsResizeTo, options.minFontSize);
  }

  const fitting = TextControllerMethods
    .reduceFontSizeChecked(changedModel, options.minFontSize);

  if (options.useAvailableArea) {
    TextControllerMethods.increaseFontSize(changedModel);
  }

  if (fitting.finalResult !== TextEditingRefusalReason.None) {
    return <TextEditingResult> {
      type: TextApplianceResult.Failure,
      model: model,
      reason: fitting.finalResult,
      position: TextControllerMethods.locateProblemPosition(
          changedModel, fitting.finalResult),
    };
  }
  else {
    return <TextEditingResult> {
      type: TextApplianceResult.Success,
      model: changedModel,
      reason: fitting.fixedResult,
    };
  }
}

export const ZoomModeFit: TextController = {
  setColor: TextControllerBase.setColor,
  setBgColor: TextControllerBase.setBgColor,
  setHorizontalAlignment: TextControllerBase.setHorizontalAlignment,
  setVerticalAlignment: TextControllerBase.setVerticalAlignment,

  setAvailableArea: (
    model: Text,
    size: TextArea,
    rawOptions?: ControllerOptions
  ): TextEditingResult =>
  {
    const options = normalizedOptions(rawOptions);
    if (!options.allowsResizeTo) {
      return TextControllerBase.setAvailableArea(model, size);
    }

    const changedModel = Utils.deepCopy(model);
    changedModel.availableArea = size;

    return fitChangedModel(model, changedModel, options);
  },

  setText: (
    model: Text,
    newText: Array<TextChunk>,
    rawOptions?: ControllerOptions
  ): TextEditingResult =>
  {
    if (TextControllerMethods.exceedsMaxTextLength(newText)) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: TextEditingRefusalReason.TextTooLong,
        model: model,
      };
    }

    const changedModel = Utils.deepCopy(model);
    changedModel.text = TextControllerMethods.removeVariationSelectors(newText);

    return fitChangedModel(model, changedModel, normalizedOptions(rawOptions));
  },

  setFont: (
    model: Text,
    fontDescription: FontDescription,
    rawOptions?: ControllerOptions
  ): TextEditingResult =>
  {
    const changedModel = updateFont(Utils.deepCopy(model), fontDescription);

    return fitChangedModel(model, changedModel, normalizedOptions(rawOptions));
  },

  setEffects: (
    model: Text,
    effects: TextEffects,
    rawOptions?: ControllerOptions
  ): TextEditingResult =>
  {
    const changedModel = Utils.deepCopy(model);
    changedModel.effects = effects;

    return fitChangedModel(model, changedModel, normalizedOptions(rawOptions));
  },

  setFontSize: (
    model: Text,
    fontSize: number,
    rawOptions?: ControllerOptions
  ): TextEditingResult =>
  {
    const options = normalizedOptions(rawOptions);
    const cappedFontSize = Math.min(fontSize, options.maxFontSize);

    let workingModel = Utils.deepCopy(model);
    workingModel.fontSize = cappedFontSize;

    if (workingModel.fontSize < options.minFontSize) {
      workingModel.fontSize = options.minFontSize;
      return <TextEditingResult> {
        type: TextApplianceResult.Success,
        model: workingModel,
        reason: TextEditingRefusalReason.MinFontSizeUndershot,
      };
    }

    if (options.allowsResizeTo) {
      return fitChangedModelResizingArea(
          model, workingModel, options.allowsResizeTo, options.minFontSize);
    }

    const fitting = TextControllerMethods
      .reduceFontSizeChecked(workingModel, options.minFontSize);

    if (options.useAvailableArea) {
      TextControllerMethods.increaseFontSize(workingModel);
    }

    return <TextEditingResult> {
      type: TextApplianceResult.Success,
      model: workingModel,
      reason: fitting.fixedResult,
    };
  }
};

export function zoomModeFitWith(
  currentFittingOptions: () => ControllerOptions): TextController
{
  type Change<T> =
      (model: Text, value: T, options?: ControllerOptions) => TextEditingResult;
  const withFittingOptions = <T>(change: Change<T>): Change<T> =>
      (model, value, options) =>
          change(model, value, {...options, ...currentFittingOptions()});

  return {
    ...ZoomModeFit,
    setAvailableArea: withFittingOptions(ZoomModeFit.setAvailableArea),
    setText: withFittingOptions(ZoomModeFit.setText),
    setFont: withFittingOptions(ZoomModeFit.setFont),
    setEffects: withFittingOptions(ZoomModeFit.setEffects),
    setFontSize: withFittingOptions(ZoomModeFit.setFontSize),
  };
}

